if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopThumbnailMql = window.matchMedia('(min-width: 990px)');
        if (!this.elements.thumbnails) return;

        this.thumbnailViewport = this.elements.thumbnails.querySelector('[data-thumbnail-viewport]');
        this.thumbnailList = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        this.thumbnailScrollButtons = {
          prev: this.elements.thumbnails.querySelector('[data-thumbnail-direction="prev"]'),
          next: this.elements.thumbnails.querySelector('[data-thumbnail-direction="next"]'),
        };

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.bindThumbnailScrollButtons();
        this.bindThumbnailViewport();
        this.updateThumbnailViewport();
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const currentElement = event.detail?.currentElement;
        if (!currentElement?.dataset?.mediaId) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const { suppressScroll = false } = options;
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (suppressScroll) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail?.dataset?.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.isDesktopThumbnailLayout()) {
          this.scrollThumbnailIntoView(thumbnail);
          this.updateThumbnailScrollButtons();
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      bindThumbnailScrollButtons() {
        Object.entries(this.thumbnailScrollButtons).forEach(([direction, button]) => {
          if (!button) return;
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.scrollThumbnailList(direction === 'next' ? 1 : -1);
          });
        });
      }

      bindThumbnailViewport() {
        if (this.thumbnailViewport) {
          this.thumbnailViewport.addEventListener('scroll', this.updateThumbnailScrollButtons.bind(this));
        }

        this.thumbnailResizeObserver = new ResizeObserver(() => this.updateThumbnailViewport());
        if (this.elements.viewer?.slider) {
          this.thumbnailResizeObserver.observe(this.elements.viewer.slider);
        }
        if (this.thumbnailViewport) {
          this.thumbnailResizeObserver.observe(this.thumbnailViewport);
        }

        const handleBreakpointChange = this.updateThumbnailViewport.bind(this);
        if (this.desktopThumbnailMql.addEventListener) {
          this.desktopThumbnailMql.addEventListener('change', handleBreakpointChange);
        } else {
          this.desktopThumbnailMql.addListener(handleBreakpointChange);
        }
      }

      isDesktopThumbnailLayout() {
        return this.desktopThumbnailMql.matches && this.thumbnailViewport && this.thumbnailList;
      }

      updateThumbnailViewport() {
        if (!this.thumbnailViewport || !this.thumbnailList || !this.elements.viewer?.slider) return;

        if (!this.isDesktopThumbnailLayout()) {
          this.thumbnailViewport.style.maxHeight = '';
          this.thumbnailViewport.scrollTop = 0;
          Object.values(this.thumbnailScrollButtons).forEach((button) => button?.setAttribute('hidden', 'hidden'));
          return;
        }

        const viewerHeight = this.elements.viewer.slider.clientHeight;
        this.thumbnailViewport.style.maxHeight = `${viewerHeight}px`;

        const hasOverflow = this.thumbnailList.scrollHeight > this.thumbnailViewport.clientHeight + 1;
        Object.values(this.thumbnailScrollButtons).forEach((button) => {
          if (!button) return;
          if (hasOverflow) {
            button.removeAttribute('hidden');
          } else {
            button.setAttribute('hidden', 'hidden');
          }
        });

        this.updateThumbnailScrollButtons();
      }

      updateThumbnailScrollButtons() {
        if (!this.isDesktopThumbnailLayout()) return;

        const maxScrollTop = this.thumbnailViewport.scrollHeight - this.thumbnailViewport.clientHeight;
        const atTop = this.thumbnailViewport.scrollTop <= 1;
        const atBottom = this.thumbnailViewport.scrollTop >= maxScrollTop - 1;

        if (this.thumbnailScrollButtons.prev) {
          this.thumbnailScrollButtons.prev.toggleAttribute('disabled', atTop);
        }
        if (this.thumbnailScrollButtons.next) {
          this.thumbnailScrollButtons.next.toggleAttribute('disabled', atBottom || maxScrollTop <= 0);
        }
      }

      getThumbnailScrollAmount() {
        const firstVisibleThumbnail = this.thumbnailList?.querySelector('.thumbnail-list__item');
        if (!firstVisibleThumbnail) return 0;

        const rowGap = parseFloat(window.getComputedStyle(this.thumbnailList).rowGap || '0');
        return firstVisibleThumbnail.offsetHeight * 3 + rowGap * 2;
      }

      scrollThumbnailList(direction) {
        if (!this.isDesktopThumbnailLayout()) return;

        const scrollAmount = this.getThumbnailScrollAmount();
        if (!scrollAmount) return;

        this.thumbnailViewport.scrollBy({
          top: direction * scrollAmount,
          behavior: 'smooth',
        });
      }

      scrollThumbnailIntoView(thumbnail) {
        if (!this.isDesktopThumbnailLayout()) return;

        const thumbnailTop = thumbnail.offsetTop;
        const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
        const viewportTop = this.thumbnailViewport.scrollTop;
        const viewportBottom = viewportTop + this.thumbnailViewport.clientHeight;
        const offset = 10;

        if (thumbnailTop < viewportTop + offset) {
          this.thumbnailViewport.scrollTo({
            top: Math.max(thumbnailTop - offset, 0),
            behavior: 'smooth',
          });
        } else if (thumbnailBottom > viewportBottom - offset) {
          this.thumbnailViewport.scrollTo({
            top: thumbnailBottom - this.thumbnailViewport.clientHeight + offset,
            behavior: 'smooth',
          });
        }
      }
    }
  );
}
